import { Box, Button, Grid, IconButton, Paper, TextField, Tooltip } from '@mui/material'
import React from 'react'
import Header from '../../Header';
import i18next from 'i18next';
import { Trans } from 'react-i18next';
import { useState } from 'react';
import { useNotify } from '../../Notify/NotifyProvider'
import request from '../../../helpers/myAxios'
import { useRef } from 'react';
import { useEffect } from 'react';
import CardEditorModal from './CardEditorModal';

import StyledReactDataGrid, { handleExportToCsv } from '../../tables/StyledReactDataGrid'
import { useCallback } from 'react';
import { TypeComputedProps, TypeOnSelectionChangeArg } from '@inovua/reactdatagrid-community/types/TypeDataGridProps';
import { MutableRefObject } from 'react';
import { SaveAlt } from '@mui/icons-material';
import { useConfirm } from '../../modals/ConfirmModal';
import { Card } from '../../../types/Card';

const maxViewWidth = 1200;
var doit: any;

interface ManageUserViewProps {
}

function ManageUserView(props: ManageUserViewProps) {
    const notify = useNotify();
    const confirm = useConfirm();
    const parentRef = useRef<HTMLDivElement>(null);

    const initialFilter: any = {}
    const [gridRef, setGridRef] = useState<MutableRefObject<TypeComputedProps> | null>(null);
    const [filter, setFilter] = useState<any>(initialFilter);
    const [cardRows, setCardRows] = useState<Card[]>([]);
    const [selectedCards, setSelectedCards] = useState<any>(null);

    const [editCard, setEditCard] = useState<Card | null>(null);
    const [openCardEditor, setOpenCardEditor] = useState(false);

    const handleSearch = () => {

        let params = { ...filter };

        for (const key of Object.keys(params)) {
            if (!params[key])
                delete params[key];
        }

        request().get('/card', { params: params }).then((res) => {
            setCardRows(res.data);
        }).catch((err) => {
            if (err.response && err.response.status === 404)
                setCardRows([]);
            else
                notify.error(i18next.t('ObjectDialogs.msgNoObjectFound'));
        });
    }

    const forceRender = () => {
        setCardRows([...cardRows]);
    }

    //Force recalculate table size on first render
    useEffect(() => {
        forceRender();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [parentRef]);

    useEffect(() => {
        handleSearch();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    //Force redraw on browser size change
    window.onresize = function () {
        clearTimeout(doit);
        doit = setTimeout(() => { forceRender(); }, 100);
    };

    const handleReset = () => {
        setFilter(initialFilter);
        setCardRows([]);
    }

    const handleChange = (event: any) => {
        setFilter({ ...filter, [event.target.id]: event.target.value });
    }

    const handleKeyPress = (e: any) => {
        if (e.key === 'Enter')
            handleSearch();
    }

    const handleAdd = () => {
        setEditCard(null);
        setOpenCardEditor(true);
    }

    const handleDelete = () => {
        if (selectedCards && selectedCards[0]) {
            confirm({
                title: i18next.t('ManageCardView.msgConfirmTitle'),
                description: i18next.t('ManageCardView.msgConfirmDelete') + ' (' + selectedCards[0].code + ')',
                confirmationText: i18next.t('buttons.yes'),
                cancellationText: i18next.t('buttons.no')
            }).then(() => {
                request().delete('/card/' + selectedCards[0]._id).then((res) => {
                    handleSearch();
                    notify.success(i18next.t('ManageCardView.msgCardDeleted'));
                }).catch((err) => {
                    notify.error(i18next.t('ManageCardView.msgNoCardFound'));
                });
            })
        }
    }

    const handleEdit = () => {
        if (selectedCards && selectedCards[0]) {
            if (selectedCards[0]?._id) {
                setEditCard(selectedCards[0]);
                setOpenCardEditor(true);
            }
        }
    }

    let parentHeight = 500;
    if (parentRef && parentRef.current && parentRef.current.offsetHeight)
        parentHeight = parentRef.current.offsetHeight;

    const columns = [
        { name: 'code', defaultFlex: 1, header: i18next.t('ManageCardView.code'), maxWidth: 120 },
        {
            name: 'ownerFirstName', defaultFlex: 1, header: i18next.t('ManageCardView.owner'),
            render: ({ data }: { data: Card }) => (data.owner?.firstName ?? '') + ' ' + (data.owner?.lastName ?? '')
        },
        {
            name: 'ownerNote', defaultFlex: 1, header: i18next.t('ManageCardView.note'),
            render: ({ data }: { data: Card }) => (data.owner?.note ?? '')
        },
        { name: 'status', header: i18next.t('ManageCardView.status'), defaultWidth: 80 }
    ];

    const handleSelectionChange = useCallback(({ data, selected }: TypeOnSelectionChangeArg) => {
        setSelectedCards(data);
    }, [])

    const onDoubleRowClick = useCallback((rowProps, event) => {
        if (rowProps.data) {
            setEditCard(rowProps.data);
            setOpenCardEditor(true);
        }
    }, [])


    const handleExport = () => {
        handleExportToCsv(gridRef, 'exportUsers.csv');
    }

    return (
        <Box sx={{ display: 'flex', height: '100%', width: '100%', flexFlow: 'column' }}>
            <Header title={i18next.t("ManageCardView.title")} >
                <Button sx={{ ml: 2, mr: 2 }} variant='contained' color='primary' onClick={handleAdd}><Trans i18nKey='buttons.add' /></Button>
            </Header>
            <Box sx={{ px: 2, pt: 2, pb: 1, width: '100%' }}>
                <Paper sx={{ p: 1, width: '100%' }} elevation={3}>
                    <Grid container spacing={1}>
                        <Grid item xs={4}>
                            <TextField id="code" fullWidth label={i18next.t('ManageCardView.code')} size='small'
                                value={filter.code ?? ''} onChange={handleChange} variant="filled" onKeyDown={handleKeyPress} />
                        </Grid>
                        <Grid item xs={4}>
                            <TextField id="keyWord" fullWidth label={i18next.t('ManageCardView.keyWord')} size='small'
                                value={filter.keyWord ?? ''} onChange={handleChange} variant="filled" onKeyDown={handleKeyPress} />
                        </Grid>

                        <Grid item xs={4} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                            <Button variant='contained' onClick={handleSearch}><Trans i18nKey='buttons.search' /></Button>
                            <Button style={{ marginLeft: '8px' }} variant='outlined' onClick={handleReset}><Trans i18nKey='buttons.reset' /></Button>
                        </Grid>
                    </Grid>
                </Paper>
            </Box>
            <Box sx={{ flexGrow: 1, width: '100%', height: '100%', px: 2 }} ref={parentRef}>
                <StyledReactDataGrid
                    handle={setGridRef}
                    idProperty="id"
                    columns={columns}
                    style={{ height: parentHeight - 5 }}
                    dataSource={cardRows}
                    enableSelection
                    onSelectionChange={handleSelectionChange}
                    onDoubleRowClick={onDoubleRowClick}
                    selected={selectedCards}
                />
            </Box>
            <Box sx={{ display: 'flex', pb: 1, justifyContent: 'flex-end' }}>
                <Box style={{ flexGrow: 1 }} />
                <Button disabled={selectedCards ? false : true} sx={{ ml: '8px' }} variant='contained' onClick={handleDelete}><Trans i18nKey='buttons.delete' /></Button>
                <Button disabled={selectedCards ? false : true} sx={{ ml: '8px' }} variant='contained' onClick={handleEdit}><Trans i18nKey='buttons.edit' /></Button>
            </Box>
            <CardEditorModal open={openCardEditor} editCard={editCard} onAbort={() => { setOpenCardEditor(false); }} onSave={() => { setOpenCardEditor(false); handleSearch(); }} />
        </Box>
    )
}

export default ManageUserView

